import type { DataSource } from '../../types/siteRag';
import type { SourceRoleTruthFinding } from './sourceRoleTruthGuard';

function hasVerifiedSiteBalance(clause: string, references: readonly DataSource[]): boolean {
  return [...clause.matchAll(/\[Ref\s*:?\s*(\d+(?:\s*,\s*(?:Ref\s*:?\s*)?\d+)*)\]/gi)]
    .flatMap(match => match[1]!.match(/\d+/g) ?? [])
    .some(index => {
      const reference = references[Number(index) - 1];
      if (!reference || reference.needsVerification || reference.qualification !== 'verified'
        || !['verified-professional', 'authoritative-government'].includes(reference.sourceGrade ?? '')) return false;
      const description = `${reference.field} ${reference.source}`;
      return /\b(?:site[- ]rated|site[- ]specific|project[- ]specific)\b/i.test(description)
        && /\b(?:net\s+(?:heat(?:\s+and\s+load)?|load)\s+balance|performance\s+(?:guarantee|test))\b/i.test(description)
        && !/\b(?:proposal|proposed|preliminary|indicative|generic|peer|comparable|brochure)\b/i.test(description)
        && !!(reference.documentId || reference.url);
    });
}

/** A proposed equipment list does not itself establish guaranteed load delivery.
 * This checks only the adopted guarantee sentence; verified site performance,
 * attributed proposals and explicit uncertainty remain for ordinary grounding. */
export function equipmentGuaranteeSourceScopeFindings(
  text: string,
  sectionId: string,
  references: readonly DataSource[] = [],
): SourceRoleTruthFinding[] {
  const findings: SourceRoleTruthFinding[] = [];
  for (const sentence of text.split(/(?<=[.!?])(?<!\bU\.S\.)\s+|\n+/)) {
    for (const clause of sentence.split(/;\s*|,?\s+(?:whereas|however)\s+/i)) {
      const assertion = /\b(?:proposed|planned)\b[^.!?;]{0,220}\b(?:turbines?|generators?|gensets?|BESS|batter(?:y|ies))\b[^.!?;]{0,200}\b(?:to\s+guarantee|will\s+guarantee|guarantees)\s+(?:the\s+)?\d[\d,.]*\s*MW\s+(?:(?:IT|facility|campus|data[- ]cent(?:er|re))[- ]?)?load\b/i.exec(clause)
        ?? /\b(?:proposed|planned|proposes?)\b[^.!?;]{0,230}\b\d[\d,.]*\s*MW\s+guaranteed\s+block\b/i.exec(clause);
      if (!assertion || !/\b(?:turbines?|generators?|gensets?|BESS|batter(?:y|ies))\b/i.test(clause) || /\?(?:\s*\[Ref[^\]]+\])*\s*$/.test(clause)) continue;
      const prefix = clause.slice(0, assertion.index);
      const suffix = clause.slice(assertion.index + assertion[0].length);
      const qualified = /\b(?:if|whether|not\s+true\s+that|incorrect\s+(?:that|to\s+(?:say|claim)\s+that)|no\s+evidence\s+that|cannot\s+(?:confirm|establish)\s+that)\b/i.test(prefix)
        || /\b(?:according\s+to|(?:document|proposal|developer|owner)\b[^.!?;]{0,55}\b(?:states?|reports?|claims?|says?|calls?|describes?))\b/i.test(prefix)
        || /\b(?:owner|developer)\b[^.!?;]{0,60}\bplanning (?:case|assumption)\b/i.test(prefix)
        || /\b(?:guarantee|performance|net\s+(?:heat|load)\s+balance)\b[^.!?;]{0,45}\b(?:unverified|unconfirmed|not\s+(?:verified|confirmed|established))\b/i.test(suffix)
        || /\b(?:peer|comparable|another|other)\s+(?:site|project|plant)\b/i.test(clause)
          && !/\b(?:this|our|subject)\s+(?:site|project|plant)\b/i.test(clause);
      if (qualified || hasVerifiedSiteBalance(clause, references)) continue;
      const expected = 'Proposed equipment counts and battery power/energy ratings do not establish guaranteed delivery of the declared load. Keep the proposal conditional until site-rated net heat/load balance, auxiliaries, reserves, battery duration and contractual performance are verified.';
      findings.push({ kind: 'equipment-count-redundancy', severity: 'block', sectionId,
        excerpt: clause, observed: assertion[0], expected, message: expected,
        sourcePaths: ['references', 'projectIntent.phases'] });
    }
  }
  return findings;
}
